import { useState } from "react";
import { useTranslation } from "react-i18next";

import { HistoryDrawer } from "@/components/HistoryDrawer";

import { useItemHistory, useRecentHistory } from "../api";
import type { CatalogueItem, CatalogueKind } from "../types";
import { DeleteDialog } from "./DeleteDialog";
import { ItemSheet } from "./ItemSheet";
import { RestoreButton } from "./RestoreDialog";

type Values = Record<string, string | boolean | string[]>;

/**
 * The page's dialogs — create/edit sheet, deletion, item history, recent
 * history — opened from any row of any tab, one at a time.
 */
export function useCatalogueDialogs() {
  const { t } = useTranslation();
  const [sheet, setSheet] = useState<{
    kind: CatalogueKind;
    item: CatalogueItem | null;
    defaults?: Values;
  } | null>(null);
  const [deleting, setDeleting] = useState<{ kind: CatalogueKind; item: CatalogueItem } | null>(
    null,
  );
  const [historyOf, setHistoryOf] = useState<{
    kind: CatalogueKind;
    item: CatalogueItem;
  } | null>(null);
  const [recentOf, setRecentOf] = useState<CatalogueKind | null>(null);

  const history = useItemHistory(historyOf?.kind ?? "thematic", historyOf?.item.id ?? null);
  const recent = useRecentHistory(recentOf ?? "thematic", recentOf !== null);

  return {
    openAdd: (kind: CatalogueKind, defaults?: Values) =>
      setSheet({ kind, item: null, defaults }),
    openEdit: (kind: CatalogueKind, item: CatalogueItem) => setSheet({ kind, item }),
    openDelete: (kind: CatalogueKind, item: CatalogueItem) => setDeleting({ kind, item }),
    openHistory: (kind: CatalogueKind, item: CatalogueItem) => setHistoryOf({ kind, item }),
    openRecent: (kind: CatalogueKind) => setRecentOf(kind),
    elements: (
      <>
        {sheet && (
          <ItemSheet
            key={`${sheet.kind}-${sheet.item?.id ?? "new"}`}
            kind={sheet.kind}
            item={sheet.item}
            defaults={sheet.defaults}
            onClose={() => setSheet(null)}
          />
        )}
        {deleting && (
          <DeleteDialog
            kind={deleting.kind}
            item={deleting.item}
            onClose={() => setDeleting(null)}
          />
        )}
        {historyOf && (
          <HistoryDrawer
            title={historyOf.item.name}
            onClose={() => setHistoryOf(null)}
            query={history}
          />
        )}
        {recentOf && (
          <HistoryDrawer
            title={t("catalogue.recentTitle")}
            onClose={() => setRecentOf(null)}
            query={recent}
            entryActions={(entry) =>
              entry.restore_id ? <RestoreButton entry={entry} /> : null
            }
          />
        )}
      </>
    ),
  };
}

export type CatalogueDialogs = ReturnType<typeof useCatalogueDialogs>;
